import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Text, ActionIcon, Button } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { useBots } from '../../../lib/bots';
import { flowErrorMessage } from '../../../lib/flow-limit';
import { BotSwitcher } from '../../../components/BotSwitcher';
import { FlowDrawer } from '../index';
import { ENTRY_KINDS, entryPointDefinition, triggerType, type EntryKind } from '../entry-templates';
import './workspace.css';
import { flowAskedFor } from '../builder/components/catalog';

/**
 * The Flows workspace: the bot's flows in a sidebar on the right, the work
 * in the content area. A click on a flow opens it in the builder; the gear
 * opens its settings (name, trigger, on or off). The sidebar groups the
 * entry points a bot is built around (first contact, greeting, default,
 * after resolution) above the flows keywords start.
 */
export default function FlowsWorkspace() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { pathname } = useLocation();
  const { id: currentId } = useParams();
  const [collapsed, setCollapsed] = useState(false);
  /*
   * The sidebar fills the height of the screen: from wherever its top sits
   * (below the page's banners at the top of the page, 12px when it sticks
   * while scrolling) down to the bottom of the viewport.
   */
  const asideRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const fit = () => {
      const el = asideRef.current;
      if (!el) return;
      const top = Math.max(12, el.getBoundingClientRect().top);
      el.style.height = `calc(100vh - ${Math.round(top) + 12}px)`;
    };
    fit();
    // The shell scrolls an inner container, not the window: capture catches both.
    window.addEventListener('resize', fit);
    window.addEventListener('scroll', fit, { passive: true, capture: true });
    return () => { window.removeEventListener('resize', fit); window.removeEventListener('scroll', fit, { capture: true }); };
  }, []);
  const [editing, setEditing] = useState<flows.Flow | 'new' | null>(null);
  const [query, setQuery] = useState('');
  const list = useQuery({ queryKey: ['flows'], queryFn: () => flows.listFlows() });
  // One chatbot's flows at a time. With a single chatbot `owns` is true for
  // every flow; the chooser below still shows which chatbot that is, with the
  // way to add another.
  const { active, owns } = useBots();
  const all = useMemo(() => (list.data?.data ?? []).filter(owns), [list.data, owns]);

  const byEntry = useMemo(() => {
    const map = new Map<EntryKind, flows.Flow>();
    for (const f of all) {
      const type = triggerType(f.definition) as EntryKind;
      if (ENTRY_KINDS.includes(type) && !map.has(type)) map.set(type, f);
    }
    return map;
  }, [all]);
  const others = useMemo(() => {
    const entryIds = new Set([...byEntry.values()].map((f) => f.id));
    const q = query.trim().toLowerCase();
    return all
      .filter((f) => !entryIds.has(f.id))
      .filter((f) => !q || f.name.toLowerCase().includes(q))
      .sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''));
  }, [all, byEntry, query]);

  const setUp = useMutation({
    mutationFn: (kind: EntryKind) => flows.createFlow({
      name: t(`flows.entry.${kind}.name`),
      definition: entryPointDefinition(kind, t),
      is_active: kind !== 'default',
      bot_id: active?.id
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['flows'] });
      nav(`/flows/${res.data.id}/builder`);
    },
    onError: (e) => notifyError(flowErrorMessage(e, t), t('flows.title'))
  });
  const createWithAi = useMutation({
    mutationFn: () => flows.createFlow({
      name: t('flows.assistant.newFlowName'),
      definition: { version: 2, trigger: { type: 'always' }, nodes: [], edges: [] },
      is_active: false,
      bot_id: active?.id
    }),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ['flows'] }); nav(`/flows/${res.data.id}/builder?assistant=1`); },
    onError: (e) => notifyError(flowErrorMessage(e, t), t('flows.title'))
  });

  const onBuilder = pathname.endsWith('/builder');
  const open = (f: flows.Flow) => nav(`/flows/${f.id}/builder`);

  // The builder takes the whole screen and carries its own flows drawer.
  if (onBuilder) return <Outlet />;

  return (
    <div className={`rr-flows-ws${collapsed ? ' is-collapsed' : ''}${onBuilder ? ' is-builder' : ''}`}>
      <main className="rr-flows-ws__content">
        <Outlet />
      </main>
      <aside ref={asideRef} className="rr-flows-ws__side" aria-label={t('flows.ws.sidebar')} data-guide="flows-sidebar">
        <div className="rr-flows-ws__side-head">
          {!collapsed && <Text role="bodySmall" fw={700}>{t('flows.ws.sidebar')}</Text>}
          <Tooltip label={collapsed ? t('flows.ws.expand') : t('flows.ws.collapse')} position="left">
            <ActionIcon variant="subtle" size="sm" aria-label={collapsed ? t('flows.ws.expand') : t('flows.ws.collapse')} onClick={() => setCollapsed((v) => !v)}>
              {collapsed ? '‹' : '›'}
            </ActionIcon>
          </Tooltip>
        </div>
        {!collapsed && (
          <>
            <BotSwitcher description={t('bots.switcher.flowsHint')} always withAdd />
            <div className="rr-flows-ws__group">
              <div className="rr-flows-ws__group-title">{t('flows.ws.entryPoints')}</div>
              {ENTRY_KINDS.map((kind) => {
                const f = byEntry.get(kind);
                return f ? (
                  <FlowRow key={kind} flow={f} kindLabel={t(`flows.entry.${kind}.name`)} active={f.id === currentId} onOpen={() => open(f)} onConfigure={() => setEditing(f)} t={t} />
                ) : (
                  // Until the list has loaded, nothing is known to be missing: a click then would create a duplicate.
                  <button key={kind} type="button" className="rr-flows-ws__row rr-flows-ws__row--missing" onClick={() => (kind === 'default' ? nav('/knowledge') : setUp.mutate(kind))} disabled={setUp.isPending || list.isPending} aria-busy={list.isPending || undefined}>
                    <span className="rr-flows-ws__dot is-missing" aria-hidden />
                    <span className="rr-flows-ws__name">{t(`flows.entry.${kind}.name`)}</span>
                    {list.isPending ? null : <span className="rr-flows-ws__setup">{t('flows.ws.setUp')}</span>}
                  </button>
                );
              })}
            </div>
            <div className="rr-flows-ws__group">
              <div className="rr-flows-ws__group-title">{t('flows.ws.yourFlows')}</div>
              {all.length > 8 && (
                <TextInput aria-label={t('flows.ws.search')} placeholder={t('flows.ws.search')} value={query} onChange={(e) => setQuery(e.currentTarget.value)} />
              )}
              {others.length === 0 && !list.isPending && <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.ws.none')}</Text>}
              {others.map((f) => (
                <FlowRow key={f.id} flow={f} kindLabel={flowAskedFor(f.definition) ? t(`flows.intentBadge.short.${flowAskedFor(f.definition)}`) : undefined} active={f.id === currentId} onOpen={() => open(f)} onConfigure={() => setEditing(f)} t={t} />
              ))}
            </div>
            <div className="rr-flows-ws__foot" data-guide="flows-new">
              <Button size="xs" onClick={() => setEditing('new')}>{t('flows.actions.create')}</Button>
              <Button size="xs" variant="default" loading={createWithAi.isPending} onClick={() => createWithAi.mutate()}>{t('flows.actions.createWithAi')}</Button>
              {/* Suggest flows with AI (30 October 2026): many at once, from ideas and the knowledge base, on a page of its own. */}
              <Button size="xs" variant="default" onClick={() => nav('/flows/ideas')} data-testid="flows-suggest-ai">{t('flows.actions.suggestWithAi')}</Button>
              <Button size="xs" variant="subtle" onClick={() => nav('/flows/all')}>{t('flows.ws.allFlows')}</Button>
              <Button size="xs" variant="subtle" onClick={() => nav('/flows/calls')} data-testid="flows-calls-link">{t('flows.calls.title')}</Button>
            </div>
          </>
        )}
      </aside>
      <FlowDrawer
        flow={editing}
        cloneFrom={null}
        onClose={() => setEditing(null)}
        onSaved={() => { qc.invalidateQueries({ queryKey: ['flows'] }); if (currentId) qc.invalidateQueries({ queryKey: ['flow', currentId] }); }}
      />
    </div>
  );
}

function FlowRow({ flow, kindLabel, active, onOpen, onConfigure, t }: {
  flow: flows.Flow; kindLabel?: string; active: boolean; onOpen: () => void; onConfigure: () => void; t: (k: string) => string;
}) {
  return (
    <div className={`rr-flows-ws__row${active ? ' is-active' : ''}`}>
      <button type="button" className="rr-flows-ws__open" onClick={onOpen} title={t('flows.ws.openBuilder')}>
        <span className={`rr-flows-ws__dot ${flow.is_active ? 'is-on' : 'is-off'}`} aria-label={flow.is_active ? t('status.active') : t('status.inactive')} />
        <span className="rr-flows-ws__text">
          <span className="rr-flows-ws__name">{flow.name}</span>
          {kindLabel && kindLabel !== flow.name && <span className="rr-flows-ws__kind">{kindLabel}</span>}
        </span>
      </button>
      <Tooltip label={t('flows.ws.configure')} position="left">
        <ActionIcon variant="subtle" size="sm" aria-label={t('flows.ws.configure')} onClick={onConfigure}>
          <GearIcon />
        </ActionIcon>
      </Tooltip>
    </div>
  );
}

function GearIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="8" cy="8" r="2.2" />
      <path d="M8 1.8v1.6M8 12.6v1.6M1.8 8h1.6M12.6 8h1.6M3.6 3.6l1.1 1.1M11.3 11.3l1.1 1.1M3.6 12.4l1.1-1.1M11.3 4.7l1.1-1.1" />
    </svg>
  );
}
